/**
 * Account page — your details, and your password.
 *
 * The page used to only display name and email, with no way to change either:
 * a learner who mistyped their name at sign-up was stuck with it on their
 * teacher's roster, and one with no address could not add the address a reset
 * link needs. Editable now, with username and role left alone — one identifies
 * you at sign-in, the other is not yours to grant.
 */
import { useEffect, useState, type FormEvent } from "react";
import { useAuth } from "../auth/AuthContext";
import { ApiError, apiPost } from "../api/client";
import { AiConsentChoice, AiConsentExplainer } from "../components/AiConsentChoice";
import type { User } from "../types";

export function AccountPage() {
  const { user, applyUser } = useAuth();

  const [name, setName] = useState(user?.name ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [profileError, setProfileError] = useState("");
  const [profileNotice, setProfileNotice] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  // The account arrives after the first render, so seed the fields once it does.
  useEffect(() => {
    if (user) {
      setName(user.name ?? "");
      setEmail(user.email ?? "");
    }
  }, [user?.id]);

  const dirty =
    name.trim() !== (user?.name ?? "") ||
    email.trim() !== (user?.email ?? "");

  async function handleProfileSubmit(e: FormEvent) {
    e.preventDefault();
    setProfileError("");
    setProfileNotice("");
    setSavingProfile(true);
    try {
      const updated = await apiPost<User>("/api/auth/me", {
        name: name.trim(),
        email: email.trim() || null,
      });
      applyUser(updated);
      setProfileNotice("Saved.");
    } catch (err) {
      setProfileError(
        err instanceof ApiError ? err.message : "Couldn't save your details"
      );
    } finally {
      setSavingProfile(false);
    }
  }

  // Saved on click rather than behind a "Save" button: withdrawing consent is
  // the one action here that should take effect the instant it is chosen, not
  // once the person finds and presses a second control.
  const [consentError, setConsentError] = useState("");
  const [consentNotice, setConsentNotice] = useState("");
  const [savingConsent, setSavingConsent] = useState(false);

  async function handleConsent(granted: boolean) {
    setConsentError("");
    setConsentNotice("");
    setSavingConsent(true);
    try {
      const updated = await apiPost<User>("/api/auth/consent/ai", { granted });
      applyUser(updated);
      setConsentNotice(
        granted
          ? "Saved. Your answers will be marked by the AI."
          : "Saved. Nothing you write will leave PyDialog."
      );
    } catch (err) {
      setConsentError(
        err instanceof ApiError ? err.message : "Couldn't save your choice"
      );
    } finally {
      setSavingConsent(false);
    }
  }

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (next !== confirm) {
      setError("Those passwords don't match.");
      return;
    }
    setError("");
    setDone(false);
    setLoading(true);
    try {
      await apiPost("/api/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      setDone(true);
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't change the password");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="account-page">
      <h1>Your account</h1>

      {/* The fixed facts. Username identifies you at sign-in and role is not
          yours to grant, so neither is editable here. */}
      <div className="card">
        <dl className="account-facts">
          <div>
            <dt>Username</dt>
            <dd>{user?.username}</dd>
          </div>
          {user?.student_number && (
            <div>
              <dt>Student no.</dt>
              {/* Permanent and readable — this is how a teacher or admin refers
                  to you, so it needs to survive a name change and be legible
                  over a phone. */}
              <dd><span className="student-number">{user.student_number}</span></dd>
            </div>
          )}
          <div>
            <dt>Role</dt>
            <dd>{user?.role}</dd>
          </div>
        </dl>
      </div>

      <div className="card">
        <h2 className="account-section-title">Your details</h2>
        <p className="text-muted account-section-note">
          Your name is what your teacher sees. Your email is where a password
          reset link goes — without one, you cannot reset your own password.
        </p>
        <form onSubmit={handleProfileSubmit} className="auth-form">
          <label>
            <span className="field-label">Name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              required
            />
          </label>
          <label>
            <span className="field-label">Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
            />
          </label>
          {profileError && <p className="error-message">{profileError}</p>}
          {profileNotice && <div className="notice" role="status">{profileNotice}</div>}
          <button
            type="submit"
            className="btn btn-primary"
            disabled={savingProfile || !dirty}
          >
            {savingProfile ? "Saving…" : "Save changes"}
          </button>
        </form>
      </div>

      <div className="card" id="ai-consent">
        <h2 className="account-section-title">AI marking</h2>
        {user?.ai_consent == null && (
          <div className="notice" role="status">
            You haven't answered this yet. Until you do, your answers are not
            sent anywhere and lessons won't open.
          </div>
        )}
        <AiConsentExplainer />
        <AiConsentChoice
          value={user?.ai_consent ?? null}
          onChange={handleConsent}
          name="account-ai-consent"
          disabled={savingConsent}
        />
        {/* Dated, because "you agreed to this" is not a claim anyone should
            have to take on trust. */}
        {user?.ai_consent_at && (
          <p className="text-muted account-section-note">
            Last answered{" "}
            {new Date(user.ai_consent_at).toLocaleDateString(undefined, {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
            .
          </p>
        )}
        {consentError && <p className="error-message">{consentError}</p>}
        {consentNotice && (
          <div className="notice" role="status">{consentNotice}</div>
        )}
      </div>

      <div className="card">
        <h2 className="account-section-title">Change password</h2>
        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="error" role="alert">
              {error}
            </div>
          )}
          {done && (
            <div className="notice" role="status">
              Password updated. It takes effect the next time you sign in.
            </div>
          )}
          <label>
            <span className="field-label">Current password</span>
            <input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              required
            />
          </label>
          <label>
            <span className="field-label">New password</span>
            <input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              minLength={6}
              required
            />
            <span className="field-hint">At least 6 characters.</span>
          </label>
          <label>
            <span className="field-label">Confirm new password</span>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              minLength={6}
              required
            />
          </label>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Saving…" : "Change password"}
          </button>
        </form>
      </div>
    </div>
  );
}
